import { useCallback, useEffect, useState } from 'react'

import { getAPIClient, RequestScope } from 'api/axios'
import axios, { AxiosError } from 'axios'

interface FetchProps {
  url?: string
  method?: 'POST' | 'GET' | 'PATCH' | 'DELETE'
  shouldRun?: boolean
  // SC-10999: escopo da chamada na visão multi-empresas (ver getAPIClient)
  scope?: RequestScope['scope']
  merchantUuid?: string
}

interface StateParams {
  error: null | AxiosError | Error | string
  result: any
  status: 'idle' | 'pending' | 'settled' | 'error'
  code: null | number
}

// A API detalha o motivo do erro em `detail_error` e deixa em `message` apenas o
// resumo genérico (ex.: "Nenhum contrato foi criado."). Sem ler o `detail_error`,
// o usuário perde a única informação acionável — qual campo precisa corrigir.
// Mesma normalização já aplicada no DataService: pode vir string ou array.
const getErrorMessage = (data: any) => {
  const detail = Array.isArray(data?.detail_error)
    ? data?.detail_error[0]
    : data?.detail_error

  return detail || data?.message
}

export const useFetch = ({
  url,
  method = 'GET',
  shouldRun = false,
  scope,
  merchantUuid
}: FetchProps) => {
  const [state, setState] = useState<StateParams>({
    result: null,
    error: null,
    code: null,
    status: 'idle'
  })

  const client = getAPIClient(undefined, { scope, merchantUuid })

  const run = useCallback(
    ({
      data,
      paramUrl,
      query
    }: {
      data?: any
      paramUrl?: string
      query?: string
    }) => {
      setState({
        code: null,
        error: null,
        result: null,
        status: 'pending'
      })

      return client({
        data,
        method,
        url: paramUrl
          ? `${url}/${paramUrl}${query ? query : ''}`
          : `${url}${query ? query : ''}`
      })
        .then((response) => {
          const data = response?.data ? response?.data : {}
          setState({
            error: null,
            result: {
              data: data?.records ? data?.records : data?.record,
              message: data?.message,
              meta: data?.meta
            },
            code: response.status,
            status: 'settled'
          })
        })
        .catch((error) => {
          const errors = error as AxiosError
          setState({
            error:
              axios.isAxiosError(errors) && errors.response
                ? getErrorMessage(errors.response.data)
                : null,
            code:
              axios.isAxiosError(errors) && errors.response
                ? errors.response.status
                : 401,
            result: null,
            status: 'error'
          })
        })
    },
    []
  )

  useEffect(() => {
    if (shouldRun) run({})
    return () => {
      setState({
        code: null,
        error: null,
        result: null,
        status: 'idle'
      })
    }
  }, [shouldRun, run])

  return [run, state.result, state.error, state.status] as const
}
